import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Send, Image as ImageIcon, Trash2, ShieldAlert, EyeOff, X, ThumbsUp, Quote as QuoteIcon } from "lucide-react";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import ForumPostBody from "../components/ForumPostBody";

const EMERGENT_AUTH_BASE = "https://auth.emergentagent.com";

// Build the Emergent OAuth start URL pointing back at /thelastpost.
function emergentGoogleUrl() {
  if (typeof window === "undefined") return "#";
  const back = `${window.location.origin}/thelastpost`;
  return `${EMERGENT_AUTH_BASE}/?redirect=${encodeURIComponent(`${back}?ctx=forum&next=/thelastpost`)}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

// FastAPI returns 422 errors with `detail` as an array of objects.
// React can't render objects so blindly stuffing `detail` into state and
// rendering `{error}` crashes the whole page to white. Normalise to a
// plain string here.
function errMsg(e, fallback = "Something went wrong.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length > 0) {
    const first = d[0];
    return (first && (first.msg || first.message)) || fallback;
  }
  if (d && typeof d === "object") return d.msg || d.message || fallback;
  return e?.message || fallback;
}

export default function Forum() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [posting, setPosting] = useState(false);
  const [body, setBody] = useState("");
  const [imageUrls, setImageUrls] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const bodyRef = useRef(null);
  const [error, setError] = useState("");
  const composerRef = useRef(null);

  // Onboarding modal state
  const [nicknameInput, setNicknameInput] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Magic-link sign-in state
  const [magicEmail, setMagicEmail] = useState("");
  const [magicState, setMagicState] = useState("idle"); // idle|sending|sent|error
  const [magicError, setMagicError] = useState("");

  // Account-linking state — lets an already-signed-in member add a
  // second Google/email identity to THIS account, so a prior sign-in
  // under a different identity (which created a separate blank account)
  // can be folded back in without losing their nickname/history.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkState, setLinkState] = useState("idle"); // idle|sending|sent|error
  const [linkError, setLinkError] = useState("");

  const me = data?.me;
  const settings = data?.settings;
  const isMod = me && (me.role === "mod" || me.role === "admin");
  const needsSetup = me && me.needs_setup;

  // Note: the post-Google-OAuth `#session_id=...` exchange (both plain
  // sign-in and account-linking) is handled by <AuthCallback/> — App.js's
  // <AuthRouter> intercepts any route whose hash contains `session_id=`
  // and renders that instead, so this component never actually sees the
  // fragment on return from Google. Just load the board on mount.
  useEffect(() => {
    load(1);
    // Surface a failed Google-link attempt (e.g. the "already belongs to
    // another member" conflict) left behind by <AuthCallback/>, right
    // where the user was trying to link, instead of it vanishing silently.
    const linkErr = sessionStorage.getItem("forum_link_error");
    if (linkErr) {
      sessionStorage.removeItem("forum_link_error");
      setLinkOpen(true);
      setLinkState("error");
      setLinkError(linkErr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startGoogleLink = () => {
    sessionStorage.setItem("forum_link_intent", "1");
    window.location.href = emergentGoogleUrl();
  };

  const submitLinkEmail = async (e) => {
    e.preventDefault();
    if (!linkEmail.trim()) return;
    setLinkState("sending");
    setLinkError("");
    try {
      await api.post("/forum/auth/link/email/request", { email: linkEmail.trim() });
      setLinkState("sent");
    } catch (e2) {
      setLinkState("error");
      setLinkError(errMsg(e2, "Could not send link."));
    }
  };

  const load = async (p = page) => {
    setLoading(true);
    try {
      const r = await api.get(`/forum/posts?page=${p}`);
      setData(r.data || null);
      setPage(p);
    } catch (e) {
      setError("Could not load the board.");
    } finally {
      setLoading(false);
    }
  };

  const submitPost = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() && imageUrls.length === 0) return;
    setPosting(true);
    setError("");
    try {
      await api.post("/forum/posts", { body: body.trim(), image_urls: imageUrls });
      setBody("");
      setImageUrls([]);
      await load(1); // newest first → always reload page 1 after posting
    } catch (e2) {
      setError(errMsg(e2, "Could not post."));
    } finally {
      setPosting(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/forum/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data?.url || null;
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      if (url) setImageUrls((prev) => [...prev, url].slice(0, 4));
    } catch {
      setError("Image upload failed.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAvatarUploading(true);
    try {
      const url = await uploadImage(file);
      if (url) {
        await api.patch("/forum/me", { avatar_url: url });
        await load(page);
      }
    } catch {
      setError("Avatar upload failed.");
    } finally {
      setAvatarUploading(false);
    }
  };

  const saveNickname = async () => {
    setSavingProfile(true);
    setError("");
    try {
      await api.patch("/forum/me", { nickname: nicknameInput.trim() });
      setNicknameInput("");
      await load(page);
    } catch (e) {
      setError(errMsg(e, "Could not save nickname."));
    } finally {
      setSavingProfile(false);
    }
  };

  // 30-emoji quick palette — football + Sky Blues bias, plus everyday chat.
  const EMOJI_PALETTE = [
    "⚽", "🥅", "🏆", "🎉", "🔥", "💙",
    "👏", "🙌", "👍", "👎", "🫶", "🙏",
    "😀", "😂", "😅", "😍", "😎", "😡",
    "🤔", "😭", "🤯", "😴", "🥶", "🥳",
    "🦁", "🐐", "🍺", "🍻", "📣", "🎬",
  ];

  const insertAtCursor = (text) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + text); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      const pos = start + text.length;
      bodyRef.current.setSelectionRange(pos, pos);
    });
  };

  const promptYouTube = () => {
    const url = window.prompt("Paste a YouTube URL — it'll embed automatically when you post.");
    if (!url) return;
    const trimmed = url.trim();
    const prefix = body && !body.endsWith(" ") && !body.endsWith("\n") ? " " : "";
    insertAtCursor(prefix + trimmed + " ");
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    if (!magicEmail.trim()) return;
    setMagicState("sending");
    setMagicError("");
    try {
      await api.post("/forum/auth/magic/request", { email: magicEmail.trim() });
      setMagicState("sent");
    } catch (e2) {
      setMagicState("error");
      setMagicError(errMsg(e2, "Could not send link."));
    }
  };

  const logout = async () => {
    try {
      await api.post("/forum/auth/logout");
    } catch (_) {
      // ignore — we clear client state regardless, same as the CMS logout
    }
    setData((d) => d ? { ...d, me: null } : d);
    // Reset linking panel state so it doesn't carry over to the next
    // sign-in in the same browser session.
    setLinkOpen(false);
    setLinkEmail("");
    setLinkState("idle");
    setLinkError("");
    // The sign-in panel sits below the posts list — jump straight to it
    // so a guest (or someone who just signed out of a stuck setup screen)
    // isn't left staring at the top of a busy board.
    requestAnimationFrame(() => {
      document.getElementById("forum-signin-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/forum/posts/${id}`);
    await load(page);
  };

  const hideImage = async (postId, url) => {
    if (!window.confirm("Remove this image from the post? The rest of the conversation stays.")) return;
    try {
      const r = await api.post(`/forum/posts/${postId}/hide-image`, { url });
      setData((d) => {
        if (!d?.posts) return d;
        return {
          ...d,
          posts: d.posts.map((p) =>
            p.id === postId ? { ...p, image_urls: r.data.image_urls } : p
          ),
        };
      });
    } catch (e2) {
      setError(errMsg(e2, "Could not hide that image."));
    }
  };

  const quotePost = (p) => {
    if (!me) {
      // Bounce guests to the sign-in panel — nothing to do otherwise.
      document.getElementById("forum-signin-panel")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const author = p?.author?.nickname || "(deleted)";
    // Strip any existing "> " lines so we don't quote a quote of a quote.
    const cleaned = (p?.body || "")
      .split(/\n/)
      .filter((ln) => !/^>\s?/.test(ln))
      .join("\n")
      .trim();
    // Cap quoted text at 600 chars so the composer doesn't explode.
    const trimmed = cleaned.length > 600 ? `${cleaned.slice(0, 600).trim()}…` : cleaned;
    const quoted = trimmed
      ? `> @${author} wrote:\n${trimmed.split(/\n/).map((l) => `> ${l}`).join("\n")}\n\n`
      : `> @${author} wrote:\n> [image / video]\n\n`;
    // Prepend to existing draft (or replace if empty), focus the textarea
    // and scroll the composer into view on mobile.
    setBody((prev) => (prev ? `${quoted}${prev}` : quoted));
    setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      const ta = bodyRef.current;
      if (ta) {
        ta.focus();
        const pos = ta.value.length;
        ta.setSelectionRange(pos, pos);
      }
    }, 30);
  };

  const toggleReaction = async (postId, kind) => {
    if (!me) return; // guests can't react
    const countKey = kind === "like" ? "like_count" : "laugh_count";
    const flagKey = kind === "like" ? "liked_by_me" : "laughed_by_me";
    const endpoint = kind === "like" ? "like" : "laugh";
    const apply = (p, flipped) => ({
      ...p,
      [flagKey]: flipped,
      [countKey]: (p[countKey] || 0) + (p[flagKey] ? -1 : 1),
    });
    // Optimistic flip for snappy UX
    setData((d) => {
      if (!d?.posts) return d;
      return {
        ...d,
        posts: d.posts.map((p) => (p.id === postId ? apply(p, !p[flagKey]) : p)),
      };
    });
    try {
      const r = await api.post(`/forum/posts/${postId}/${endpoint}`);
      // Reconcile with server truth
      setData((d) => {
        if (!d?.posts) return d;
        return {
          ...d,
          posts: d.posts.map((p) =>
            p.id === postId
              ? { ...p, [flagKey]: !!r.data[flagKey], [countKey]: r.data[countKey] }
              : p
          ),
        };
      });
    } catch (e2) {
      // Roll back on failure
      setData((d) => {
        if (!d?.posts) return d;
        return {
          ...d,
          posts: d.posts.map((p) => (p.id === postId ? apply(p, !p[flagKey]) : p)),
        };
      });
      setError(errMsg(e2, `Could not register ${kind}.`));
    }
  };
  const toggleLike = (postId) => toggleReaction(postId, "like");
  const toggleLaugh = (postId) => toggleReaction(postId, "laugh");

  const shadowBan = async (userId) => {
    const reason = window.prompt("Shadow-ban reason (optional)") || "";
    await api.post(`/forum/users/${userId}/shadowban`, { reason });
    await load(page);
  };

  const banUser = async (userId) => {
    const reason = window.prompt("Ban reason (visible to user)") || "";
    await api.post(`/forum/users/${userId}/ban`, { reason });
    await load(page);
  };

  const totalPages = data?.total ? Math.max(1, Math.ceil(data.total / (data.per_page || 30))) : 1;

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-forum">
      {settings?.hide_site_header ? null : (
        <Header hideActionBar={!!settings?.hide_action_bar} hideLogo hideMobileChrome={!!settings?.hide_mobile_chrome} />
      )}
      <main className="mx-auto max-w-3xl px-5 pt-6 pb-12 sm:px-8 sm:pt-8 sm:pb-16 lg:max-w-5xl">
        {/* Banner image — sits inside the same max-w as the forum on
            desktop, edge-to-edge on mobile via negative-margin. If a
            link URL is set, the whole banner becomes a clickable anchor. */}
        {settings?.banner_image_url ? (
          (() => {
            const img = (
              <img src={settings.banner_image_url} alt="" className="block w-full" style={{ aspectRatio: "5 / 1", objectFit: "cover" }} />
            );
            const inner = <div className="overflow-hidden rounded-md bg-black sm:rounded-md" data-testid="forum-banner-image">{img}</div>;
            return (
              <div className="-mx-5 mb-5 sm:mx-0">
                {settings.banner_image_link ? (
                  <a href={settings.banner_image_link} target={/^https?:\/\//.test(settings.banner_image_link) ? "_blank" : undefined} rel="noopener noreferrer" data-testid="forum-banner-link">
                    {inner}
                  </a>
                ) : inner}
              </div>
            );
          })()
        ) : null}

        {/* Coloured-text banner */}
        {settings?.banner_text && !settings?.hide_text_banner ? (
          <div
            className="mb-6 rounded-md px-4 py-3 text-sm font-semibold text-white"
            style={{ background: settings.banner_color || "#6CABDD" }}
            data-testid="forum-banner"
          >
            {settings.banner_text}
          </div>
        ) : null}

        {!settings?.hide_title ? (
          <h1 className="font-display text-3xl font-black sm:text-4xl" data-testid="forum-title">
            {settings?.forum_title || "The LAST Post"}
          </h1>
        ) : null}
        {!settings?.hide_intro && (settings?.forum_intro || "") ? (
          <p className="mt-1 text-sm text-[var(--brand-body)]" data-testid="forum-intro">
            {settings.forum_intro}
          </p>
        ) : null}

        {loading ? (
          <div
            className="mt-6 flex min-h-[40vh] flex-col items-center justify-center gap-3 rounded-md border border-dashed border-black/10 text-center"
            data-testid="forum-loading"
          >
            <Loader2 className="h-7 w-7 animate-spin text-brand-accent" />
            <p className="text-sm text-[var(--brand-body)]">Loading…</p>
          </div>
        ) : data?.maintenance && !isMod ? (
          <div className="mt-10 rounded-md border border-dashed border-black/15 p-10 text-center">
            <ShieldAlert className="mx-auto h-8 w-8 text-[var(--brand-body)]" />
            <p className="mt-3 font-display text-lg font-bold">The forum is paused for maintenance.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">We&apos;ll be back shortly.</p>
          </div>
        ) : (
          <>
            {/* Composer — only for signed-in users with a nickname. */}
            {me && !needsSetup ? (
              <form
                onSubmit={submitPost}
                ref={composerRef}
                className="mt-6 rounded-md border border-black/10 bg-white p-4"
                data-testid="forum-composer"
              >
              <div className="flex items-start gap-3">
                <Avatar user={me} />
                <div className="min-w-0 flex-1">
                  <textarea
                    ref={bodyRef}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={`Posting as ${me.nickname}…`}
                    rows={3}
                    maxLength={4000}
                    data-testid="forum-composer-body"
                    className="block w-full resize-none rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                  />
                  {imageUrls.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {imageUrls.map((u, i) => (
                        <div key={u} className="relative h-20 w-20 overflow-hidden rounded-sm border">
                          <img src={u} alt="" className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setImageUrls((p) => p.filter((_, j) => j !== i))}
                            className="absolute right-0 top-0 rounded-bl bg-black/70 p-0.5 text-white"
                            aria-label="Remove"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="relative flex items-center gap-1">
                      {/* YouTube */}
                      <button
                        type="button"
                        onClick={promptYouTube}
                        title="Add a YouTube video"
                        aria-label="Add YouTube"
                        data-testid="forum-toolbar-youtube"
                        className="rounded-md border border-black/15 bg-white px-2 py-1 text-base leading-none hover:border-[#0A0F17]"
                      >
                        🎬
                      </button>
                      {/* Image upload */}
                      <label
                        title="Attach an image"
                        aria-label="Attach image"
                        data-testid="forum-toolbar-image"
                        className="cursor-pointer rounded-md border border-black/15 bg-white px-2 py-1 text-base leading-none hover:border-[#0A0F17]"
                      >
                        {uploadingImage ? <Loader2 size={14} className="animate-spin inline align-text-bottom" /> : "📷"}
                        <input type="file" accept="image/*" hidden onChange={handleImagePick} disabled={uploadingImage || imageUrls.length >= 4} />
                      </label>
                      {/* Emoji */}
                      <button
                        type="button"
                        onClick={() => setEmojiOpen((v) => !v)}
                        title="Add an emoji"
                        aria-label="Add emoji"
                        data-testid="forum-toolbar-emoji"
                        className={`rounded-md border border-black/15 bg-white px-2 py-1 text-base leading-none hover:border-[#0A0F17] ${emojiOpen ? "border-[#0A0F17]" : ""}`}
                      >
                        🙂
                      </button>
                      {/* GIF picker */}
                      <button
                        type="button"
                        onClick={() => setGifOpen(true)}
                        title="Add an animated GIF (powered by GIPHY)"
                        aria-label="Add a GIF"
                        data-testid="forum-toolbar-gif"
                        disabled={imageUrls.length >= 4}
                        className="rounded-md border border-black/15 bg-white px-2 py-1 font-display text-[11px] font-bold leading-none tracking-wider hover:border-[#0A0F17] disabled:opacity-50"
                      >
                        GIF
                      </button>
                      {emojiOpen ? (
                        <div
                          className="absolute left-0 top-9 z-10 grid w-56 grid-cols-6 gap-1 rounded-md border border-black/15 bg-white p-2 shadow-md"
                          data-testid="forum-emoji-palette"
                          onMouseLeave={() => setEmojiOpen(false)}
                        >
                          {EMOJI_PALETTE.map((e) => (
                            <button
                              key={e}
                              type="button"
                              onClick={() => { insertAtCursor(e); setEmojiOpen(false); }}
                              className="rounded p-1 text-lg leading-none hover:bg-black/5"
                              data-testid={`forum-emoji-${e}`}
                              aria-label={`Insert ${e}`}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="submit"
                      disabled={posting || (!body.trim() && imageUrls.length === 0)}
                      data-testid="forum-post-btn"
                      className="inline-flex items-center gap-1 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </form>
            ) : null}

            {/* Setup panel — signed in but no nickname yet. */}
            {me && needsSetup ? (
              <SetupPanel
                nickname={nicknameInput}
                setNickname={setNicknameInput}
                saving={savingProfile}
                onSave={saveNickname}
                error={error}
                onLogout={logout}
                linkOpen={linkOpen}
                setLinkOpen={setLinkOpen}
                linkEmail={linkEmail}
                setLinkEmail={setLinkEmail}
                linkState={linkState}
                linkError={linkError}
                onLinkEmailSubmit={submitLinkEmail}
                onGoogleLink={startGoogleLink}
              />
            ) : null}

            {/* Guest invite — show above posts so guests still see content. */}
            {!me ? (
              <div className="mt-6 rounded-md border border-dashed border-black/15 bg-white p-4 text-center text-sm" data-testid="forum-guest-invite">
                <span className="text-[var(--brand-body)]">Reading as a guest. </span>
                <a href="#forum-signin-panel" className="font-display font-bold text-[var(--brand-ink)] underline underline-offset-2">Click here to sign in or register to post →</a>
              </div>
            ) : null}

            {/* Action row — ← LAST shortcut on the left, signed-in controls on the right. */}
            {me && !needsSetup ? (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-[var(--brand-body)]">
                {settings?.hide_site_header ? (
                  <a href="/" className="inline-flex items-center font-display text-sm font-bold tracking-wider text-[var(--brand-ink)] underline-offset-4 hover:underline" data-testid="forum-back-home-top">
                    ← LAST
                  </a>
                ) : <span />}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="whitespace-nowrap" data-testid="forum-signed-in-as">signed in as <strong>{me.nickname}</strong></span>
                  <span aria-hidden="true">·</span>
                  <label className="cursor-pointer whitespace-nowrap underline-offset-4 hover:underline">
                    {avatarUploading ? "Uploading…" : "Change avatar"}
                    <input type="file" accept="image/*" hidden onChange={handleAvatarPick} disabled={avatarUploading} />
                  </label>
                  <span aria-hidden="true">·</span>
                  <button
                    onClick={() => setLinkOpen((v) => !v)}
                    className="whitespace-nowrap underline-offset-4 hover:underline"
                    data-testid="forum-link-toggle"
                  >
                    Link account
                  </button>
                  <span aria-hidden="true">·</span>
                  <button onClick={logout} className="whitespace-nowrap underline-offset-4 hover:underline" data-testid="forum-logout">Sign out</button>
                </div>
              </div>
            ) : null}

            {me && !needsSetup && linkOpen ? (
              <LinkAccountPanel
                email={linkEmail}
                setEmail={setLinkEmail}
                state={linkState}
                error={linkError}
                onSubmit={submitLinkEmail}
                onGoogleLink={startGoogleLink}
              />
            ) : null}

            {error ? <p className="mt-2 text-xs text-red-600" data-testid="forum-error">{error}</p> : null}

            {/* Posts list */}
            <ul className="mt-6 space-y-4" data-testid="forum-posts">
              {(data?.posts || []).map((p) => (
                <PostRow
                  key={p.id}
                  post={p}
                  me={me}
                  isMod={isMod}
                  onDelete={() => deletePost(p.id)}
                  onShadow={() => shadowBan(p.author.id)}
                  onBan={() => banUser(p.author.id)}
                  onLike={() => toggleLike(p.id)}
                  onLaugh={() => toggleLaugh(p.id)}
                  onHideImage={(url) => hideImage(p.id, url)}
                  onQuote={() => quotePost(p)}
                />
              ))}
              {(data?.posts || []).length === 0 ? (
                <li className="rounded-md border border-dashed border-black/15 p-10 text-center text-sm text-[var(--brand-body)]">
                  No posts yet — be the first.
                </li>
              ) : null}
            </ul>

            {/* Pagination */}
            {totalPages > 1 ? (
              <div className="mt-8 flex items-center justify-between" data-testid="forum-pagination">
                <button
                  onClick={() => load(page - 1)}
                  disabled={page <= 1}
                  className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-semibold disabled:opacity-40"
                >
                  ← Newer
                </button>
                <span className="text-xs text-[var(--brand-body)]">Page {page} of {totalPages}</span>
                <button
                  onClick={() => load(page + 1)}
                  disabled={page >= totalPages}
                  className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-semibold disabled:opacity-40"
                >
                  Older →
                </button>
              </div>
            ) : null}

            {/* ← LAST link below the lowest (oldest) post on this page. */}
            {settings?.hide_site_header ? (
              <div className="mt-6 flex">
                <a href="/" className="inline-flex items-center font-display text-sm font-bold tracking-wider text-[var(--brand-ink)] underline-offset-4 hover:underline" data-testid="forum-back-home-bottom">
                  ← LAST
                </a>
              </div>
            ) : null}

            {/* Sign-in panel for guests — at the bottom so it doesn't push posts down. */}
            {!me ? (
              <div id="forum-signin-panel" className="mt-8">
                <SignInPanel
                  magicEmail={magicEmail}
                  setMagicEmail={setMagicEmail}
                  magicState={magicState}
                  magicError={magicError}
                  onMagicSubmit={submitMagic}
                />
              </div>
            ) : null}
          </>
        )}
      </main>
      <Footer />
      <GifPicker
        open={gifOpen}
        onClose={() => setGifOpen(false)}
        onPick={(url) => setImageUrls((prev) => prev.length >= 4 ? prev : [...prev, url].slice(0, 4))}
      />
    </div>
  );
}

function Avatar({ user, size = 40 }) {
  const initials = (user?.nickname || "?").slice(0, 1).toUpperCase();
  if (user?.avatar_url) {
    return <img src={user.avatar_url} alt={user.nickname || ""} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <div
      className="flex items-center justify-center rounded-full bg-[#6CABDD] font-display font-bold text-white"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function PostRow({ post, me, isMod, onDelete, onShadow, onBan, onLike, onLaugh, onHideImage, onQuote }) {
  const isOwn = me && post.author && me.id === post.author.id;
  const isDeleted = !!post.deleted_at;
  const likeCount = post.like_count || 0;
  const liked = !!post.liked_by_me;
  const laughCount = post.laugh_count || 0;
  const laughed = !!post.laughed_by_me;
  return (
    <li
      className={`rounded-md border border-black/10 bg-white p-4 ${isDeleted ? "opacity-50" : ""}`}
      data-testid={`forum-post-${post.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar user={post.author} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-sm font-bold">{post.author?.nickname || "(deleted)"}</span>
            {post.author?.role === "mod" ? <span className="rounded-sm bg-[#FFA500]/20 px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B26F00]">Mod</span> : null}
            {post.author?.role === "admin" ? <span className="rounded-sm bg-[#0A0F17] px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Admin</span> : null}
            <span className="text-[11px] text-[var(--brand-body)]">{fmtDate(post.created_at)}</span>
          </div>
          {isDeleted ? (
            <p className="mt-1 text-xs italic text-[var(--brand-body)]">[Post deleted]</p>
          ) : (
            <div className="mt-1">
              <ForumPostBody body={post.body} />
              {(post.image_urls || []).length > 0 ? (
                <div className="mt-2 flex flex-col gap-3">
                  {post.image_urls.map((u) => (
                    <div key={u} className="group relative overflow-hidden rounded-md border border-black/5 self-start max-w-full">
                      <a href={u} target="_blank" rel="noreferrer" className="block">
                        <div className="overflow-hidden bg-black/5" style={{ minHeight: 160 }}>
                          <img src={u} alt="" loading="lazy" decoding="async" className="max-h-[480px] w-auto max-w-full object-contain" />
                        </div>
                      </a>
                      {isMod ? (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onHideImage(u); }}
                          title="Hide this image (keeps the post)"
                          aria-label="Hide this image"
                          data-testid={`forum-hide-image-${post.id}`}
                          className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-black/65 px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-wide text-white opacity-0 backdrop-blur-sm transition-opacity hover:bg-red-600 group-hover:opacity-100 focus-visible:opacity-100"
                        >
                          <EyeOff size={10} /> Hide
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
          {!isDeleted ? (
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-[var(--brand-body)]">
              {/* Thumbs-up — visible to everyone, click disabled for guests */}
              <button
                onClick={onLike}
                disabled={!me}
                title={me ? (liked ? "Remove like" : "Like this post") : "Sign in to like"}
                aria-pressed={liked}
                data-testid={`forum-like-${post.id}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ${
                  liked
                    ? "border-[#6CABDD] bg-[#6CABDD]/10 text-[#0A0F17]"
                    : "border-black/15 bg-white hover:border-[#0A0F17]"
                } ${!me ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <ThumbsUp size={12} className={liked ? "fill-[#6CABDD] text-[#6CABDD]" : ""} />
                <span data-testid={`forum-like-count-${post.id}`}>{likeCount}</span>
              </button>
              <button
                onClick={onLaugh}
                disabled={!me}
                title={me ? (laughed ? "Remove laugh" : "Laugh at this post") : "Sign in to react"}
                aria-pressed={laughed}
                data-testid={`forum-laugh-${post.id}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors ${
                  laughed
                    ? "border-[#FFA500] bg-[#FFA500]/15 text-[#0A0F17]"
                    : "border-black/15 bg-white hover:border-[#0A0F17]"
                } ${!me ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <span aria-hidden="true" className="text-[13px] leading-none">🤣</span>
                <span data-testid={`forum-laugh-count-${post.id}`}>{laughCount}</span>
              </button>
              <button
                onClick={onQuote}
                title={me ? "Quote this post in your reply" : "Sign in to reply"}
                data-testid={`forum-quote-${post.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-2 py-0.5 hover:border-[#0A0F17]"
              >
                <QuoteIcon size={12} /> Quote
              </button>
              {isOwn || isMod ? (
                <button onClick={onDelete} className="inline-flex items-center gap-1 hover:text-red-600" data-testid={`forum-delete-${post.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              ) : null}
              {isMod && post.author && !isOwn ? (
                <>
                  <button onClick={onShadow} className="inline-flex items-center gap-1 hover:text-[#B26F00]">
                    <EyeOff size={12} /> Shadow-ban
                  </button>
                  <button onClick={onBan} className="inline-flex items-center gap-1 hover:text-red-600">
                    <ShieldAlert size={12} /> Ban
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function SignInPanel({ magicEmail, setMagicEmail, magicState, magicError, onMagicSubmit }) {
  return (
    <div className="mt-8 rounded-md border border-black/10 bg-white p-6 sm:p-8" data-testid="forum-signin">
      <h2 className="font-display text-xl font-black">Sign in to post</h2>
      <p className="mt-1 text-sm text-[var(--brand-body)]">
        We never display your email. Pick a nickname after signing in.
      </p>

      <a
        href={emergentGoogleUrl()}
        className="mt-5 flex w-full items-center justify-center gap-3 rounded-md border border-black/15 bg-white px-4 py-3 font-display text-sm font-bold hover:border-[#0A0F17]"
        data-testid="forum-google-signin"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
          <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.346l2.583-2.582C13.464.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
        </svg>
        Continue with Google
      </a>

      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-black/10" /><span className="text-xs text-[var(--brand-body)]">or</span><div className="h-px flex-1 bg-black/10" />
      </div>

      <form onSubmit={onMagicSubmit} className="space-y-2" data-testid="forum-magic-form">
        <label className="block">
          <span className="label-kicker text-[var(--brand-body)]">Email magic link</span>
          <input
            type="email"
            required
            value={magicEmail}
            onChange={(e) => setMagicEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="forum-magic-email"
            className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
        </label>
        <button
          type="submit"
          disabled={magicState === "sending" || magicState === "sent"}
          data-testid="forum-magic-submit"
          className="w-full rounded-md bg-[#0A0F17] px-4 py-3 font-display text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
        >
          {magicState === "sending" ? "Sending…" : magicState === "sent" ? "Check your inbox" : "Send me a sign-in link"}
        </button>
        {magicState === "sent" ? (
          <p className="text-xs text-[var(--brand-body)]">If an account exists for that email, a link is on its way. It expires in 15 minutes.</p>
        ) : null}
        {magicState === "error" ? <p className="text-xs text-red-600">{magicError}</p> : null}
      </form>
    </div>
  );
}

function SetupPanel({
  nickname,
  setNickname,
  saving,
  onSave,
  error,
  onLogout,
  linkOpen,
  setLinkOpen,
  linkEmail,
  setLinkEmail,
  linkState,
  linkError,
  onLinkEmailSubmit,
  onGoogleLink,
}) {
  return (
    <div className="mt-8 rounded-md border border-black/10 bg-white p-6 sm:p-8" data-testid="forum-setup">
      <h2 className="font-display text-xl font-black">Pick a nickname</h2>
      <p className="mt-1 text-sm text-[var(--brand-body)]">2–24 characters. Letters, numbers, spaces, dashes, underscores. You can change your avatar after.</p>
      <input
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        placeholder="e.g. SkyBlueDave"
        maxLength={24}
        data-testid="forum-setup-nickname"
        className="mt-4 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-base outline-none focus:border-[#0A0F17]"
      />
      <button
        onClick={onSave}
        disabled={saving || nickname.trim().length < 2}
        data-testid="forum-setup-save"
        className="mt-3 w-full rounded-md bg-[#0A0F17] px-4 py-3 font-display text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save & start posting"}
      </button>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <div className="mt-5 border-t border-black/10 pt-4 text-center">
        <p className="text-xs text-[var(--brand-body)]">
          Not you, or already got a LAST account?{" "}
          <button
            type="button"
            onClick={() => setLinkOpen((v) => !v)}
            className="font-semibold underline underline-offset-2"
            data-testid="forum-setup-link-toggle"
          >
            Link it instead
          </button>{" "}
          or{" "}
          <button type="button" onClick={onLogout} className="font-semibold underline underline-offset-2" data-testid="forum-setup-logout">
            sign out
          </button>
          .
        </p>
      </div>

      {linkOpen ? (
        <LinkAccountPanel
          email={linkEmail}
          setEmail={setLinkEmail}
          state={linkState}
          error={linkError}
          onSubmit={onLinkEmailSubmit}
          onGoogleLink={onGoogleLink}
        />
      ) : null}
    </div>
  );
}

function LinkAccountPanel({ email, setEmail, state, error, onSubmit, onGoogleLink }) {
  return (
    <div className="mt-4 rounded-md border border-dashed border-black/15 p-4" data-testid="forum-link-panel">
      <p className="text-xs text-[var(--brand-body)]">
        Link a Google account or another email to this account so you can always get back in.
      </p>
      <button
        type="button"
        onClick={onGoogleLink}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 font-display text-xs font-bold hover:border-[#0A0F17]"
        data-testid="forum-link-google"
      >
        <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
          <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.346l2.583-2.582C13.464.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
        </svg>
        Link Google account
      </button>
      {state === "sent" ? (
        <p className="mt-3 text-xs text-[var(--brand-body)]" data-testid="forum-link-sent">
          If that email checks out, a confirm-link is on its way. It expires in 15 minutes.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-3 flex gap-2" data-testid="forum-link-email-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="another@email.com"
            data-testid="forum-link-email"
            className="min-w-0 flex-1 rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
          <button
            type="submit"
            disabled={state === "sending" || !email.trim()}
            data-testid="forum-link-email-send"
            className="shrink-0 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
          >
            {state === "sending" ? "Sending…" : "Send link"}
          </button>
        </form>
      )}
      {state === "error" ? <p className="mt-2 text-xs text-red-600" data-testid="forum-link-email-error">{error}</p> : null}
    </div>
  );
}

function GifPicker({ open, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const searchTimer = useRef(null);

  // Fetch helper — search if q given, otherwise Tenor "featured".
  const fetchPage = async (query, pos = "", append = false) => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/forum/gif/search", { params: { q: query, pos, limit: 24 } });
      const results = r.data?.results || [];
      setItems((prev) => (append ? [...prev, ...results] : results));
      setNext(r.data?.next || "");
    } catch (e2) {
      setErr(errMsg(e2, "Couldn't load GIFs."));
    } finally {
      setLoading(false);
    }
  };

  // Initial featured + debounced search when query changes.
  useEffect(() => {
    if (!open) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchPage(q.trim()), q ? 350 : 0);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-2 sm:items-center"
      onClick={onClose}
      data-testid="forum-gif-picker"
    >
      <div
        className="flex w-full max-w-lg max-h-[80vh] flex-col rounded-md bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-black/10 p-3">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search GIFs (try: celebrate, sad, dance…)"
            data-testid="forum-gif-search-input"
            className="flex-1 rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
          />
          <button
            type="button"
            onClick={onClose}
            data-testid="forum-gif-close"
            className="rounded-md border border-black/15 px-2 py-2 text-xs font-bold hover:bg-black/5"
            aria-label="Close GIF picker"
          >
            <X size={14} />
          </button>
        </div>
        <div className="min-h-[200px] flex-1 overflow-y-auto p-2">
          {err ? <p className="p-3 text-sm text-red-600">{err}</p> : null}
          {!err && items.length === 0 && !loading ? (
            <p className="p-3 text-sm text-[var(--brand-body)]">No GIFs found. Try another search.</p>
          ) : null}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {items.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => { onPick(g.url); onClose(); }}
                title={g.title}
                data-testid={`forum-gif-pick-${g.id}`}
                className="group block overflow-hidden rounded-sm border border-transparent hover:border-[#0A0F17]"
              >
                <img src={g.thumb} alt={g.title || ""} loading="lazy" className="aspect-video w-full bg-black/5 object-cover" />
              </button>
            ))}
          </div>
          {loading ? (
            <div className="flex justify-center p-3"><Loader2 size={16} className="animate-spin" /></div>
          ) : null}
          {next && !loading ? (
            <div className="flex justify-center p-3">
              <button
                type="button"
                onClick={() => fetchPage(q.trim(), next, true)}
                className="rounded-md border border-black/15 px-3 py-1.5 font-display text-xs font-bold hover:bg-black/5"
                data-testid="forum-gif-more"
              >
                Load more
              </button>
            </div>
          ) : null}
        </div>
        <div className="border-t border-black/10 px-3 py-2 text-[10px] uppercase tracking-wide text-[var(--brand-body)]">
          Powered by GIPHY
        </div>
      </div>
    </div>
  );
}

